/**
 * GeolocationServiceCard - карточка «Местоположение аптеки» в профиле (PROJECT_NOTES §37).
 * Показывает сохраненную точку на мини-карте, а ставит и уточняет ее мастер в окне: замер устройства, пин на карте
 * или телефон по QR-коду. Сохраняет мастер через /api/v2/profile/geolocation, карточка только обновляет показ.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, Dialog, DialogContent, DialogTitle, IconButton, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Close, ContentCopy, LocationOnOutlined, MyLocationOutlined, OpenInNew, PinDropOutlined } from '@mui/icons-material';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Circle, CircleMarker, MapContainer, TileLayer } from 'react-leaflet';

import { notify } from '../../../notifications';
import { copyText } from '../../../admin/mathReports/changes/copyText';
import { profileGeoBackend, SavedGeo, SavedPoint } from '../../../geolocation/geoApi';
import { formatCoords, MAX_ACCURACY } from '../../../geolocation/geoMath';
import GeoWizard from '../../../geolocation/GeoWizard';
import PhoneHandoffPanel from '../../../geolocation/PhoneHandoffPanel';
import { BORDER, MUTED, PRIMARY, TEXT, monoSx, primaryButtonSx } from '../../../geolocation/geoStyles';
import { serviceButtonGrowSx, serviceCardSx } from './serviceCardStyles';

export interface GeolocationServiceCardProps {
  pharmacyAddress?: string;
  latitude?: string | number | null;
  longitude?: string | number | null;
  accuracy?: number | null;
  /** Сервис геолокаций не ответил: «точки нет» показывать нельзя, она может быть сохранена */
  unavailable: boolean;
  onGeoUpdated?: (geo: { latitude: number; longitude: number; accuracy?: number; address?: string }) => void;
}

const PREVIEW_ZOOM = 17;
const PREVIEW_HEIGHT = 176;

// 0 в обеих координатах - так старые записи хранят «точки нет»
function toPoint(latitude: GeolocationServiceCardProps['latitude'], longitude: GeolocationServiceCardProps['longitude'], accuracy: number | null | undefined, address: string): SavedGeo | null {
  if (latitude == null || longitude == null || latitude === '' || longitude === '') return null;
  const lat = Number(latitude);
  const lng = Number(longitude);
  if (!isFinite(lat) || !isFinite(lng) || (lat === 0 && lng === 0)) return null;
  return { latitude: lat, longitude: lng, accuracy: accuracy != null && isFinite(Number(accuracy)) ? Number(accuracy) : null, address };
}

const copyButtonSx = { border: `1px solid ${BORDER}`, bgcolor: '#f8fafc', color: MUTED, p: 0.5, flexShrink: 0, '&:hover': { bgcolor: '#f1f5f9', color: TEXT } };
const mapLinkSx = { textTransform: 'none', fontSize: '0.78rem', fontWeight: 600, borderRadius: '8px', py: 0.3, px: 1.25 } as const;

const GeolocationServiceCard: React.FC<GeolocationServiceCardProps> = ({ pharmacyAddress = '', latitude, longitude, accuracy, unavailable, onGeoUpdated }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const fromProps = useMemo(() => toPoint(latitude, longitude, accuracy, pharmacyAddress), [latitude, longitude, accuracy, pharmacyAddress]);
  const [point, setPoint] = useState<SavedGeo | null>(fromProps);
  const [open, setOpen] = useState(false);
  const [previewMap, setPreviewMap] = useState<L.Map | null>(null);

  useEffect(() => { setPoint(fromProps); }, [fromProps]);

  // Карточка рисуется внутри секции, которая еще раскладывается: без пересчета размера плитки встают со сдвигом
  useEffect(() => {
    if (!previewMap) return undefined;
    const timer = window.setTimeout(() => previewMap.invalidateSize(), 200);
    return () => window.clearTimeout(timer);
  }, [previewMap]);

  const handleSaved = (geo: SavedPoint, message: string) => {
    setOpen(false);
    setPoint({ latitude: geo.latitude, longitude: geo.longitude, accuracy: geo.accuracy, address: geo.address });
    if (onGeoUpdated) onGeoUpdated({ latitude: geo.latitude, longitude: geo.longitude, accuracy: geo.accuracy, address: geo.address });
    if (geo.address) notify.success(message);
    else notify.warning(message);
  };

  const copy = async (text: string, done: string) => {
    if (await copyText(text)) notify.success(done);
    else notify.error('Не удалось скопировать');
  };

  const accurate = Boolean(point && point.accuracy != null && point.accuracy <= MAX_ACCURACY);
  const status = point
    ? accurate
      ? { label: 'Координаты подтверждены', color: '#166534', border: '#bbf7d0' }
      : { label: 'Нужно уточнить', color: '#b45309', border: '#fed7aa' }
    : unavailable
      ? { label: 'Нет связи с сервисом', color: '#b45309', border: '#fed7aa' }
      : { label: 'Не указано', color: '#475569', border: '#cbd5e1' };

  return (
    <Box sx={{ ...serviceCardSx, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: '1 1 280px', minWidth: 0 }}>
          <Box sx={{ width: 44, height: 44, borderRadius: '12px', bgcolor: 'rgba(21,112,239,0.1)', color: PRIMARY, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <MyLocationOutlined sx={{ fontSize: 24 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: TEXT, fontSize: '1rem' }}>Местоположение аптеки</Typography>
              <Chip
                label={status.label}
                size="small"
                sx={{ fontWeight: 600, fontSize: '0.74rem', height: 22, bgcolor: '#fff', color: status.color, border: `1px solid ${status.border}` }}
              />
            </Box>
            <Typography variant="body2" sx={{ color: MUTED, mt: 0.5, fontSize: '0.85rem' }}>
              Точка входа в аптеку на карте. Поставить ее можно с этого компьютера или с телефона по QR-коду: телефон определяет место точнее.
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<PinDropOutlined sx={{ fontSize: 18 }} />}
          onClick={() => setOpen(true)}
          sx={{ ...primaryButtonSx, ...serviceButtonGrowSx }}
        >
          {point ? 'Уточнить на карте' : 'Указать на карте'}
        </Button>
      </Box>

      {point ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'stretch' }}>
          {/* Мини-карта только показывает точку: двигать ее - в мастере, поэтому нажатие открывает его */}
          <Box
            onClick={() => setOpen(true)}
            sx={{
              flex: '1 1 280px', height: PREVIEW_HEIGHT, borderRadius: '12px', overflow: 'hidden', border: '1px solid #cbd5e1',
              bgcolor: '#f1f5f9', cursor: 'pointer', position: 'relative',
              '& .leaflet-container': { width: '100%', height: '100%', fontFamily: 'inherit', bgcolor: '#f1f5f9', cursor: 'pointer' },
              '& .leaflet-control-attribution': { fontSize: '9px', bgcolor: 'rgba(255,255,255,0.8)', px: 0.75 }
            }}
          >
            <MapContainer
              key={`${point.latitude},${point.longitude}`}
              ref={setPreviewMap}
              center={[point.latitude, point.longitude]}
              zoom={PREVIEW_ZOOM}
              zoomControl={false}
              dragging={false}
              scrollWheelZoom={false}
              doubleClickZoom={false}
              touchZoom={false}
              boxZoom={false}
              keyboard={false}
            >
              <TileLayer
                url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a>'
              />
              {point.accuracy != null && (
                <Circle center={[point.latitude, point.longitude]} radius={point.accuracy} pathOptions={{ color: PRIMARY, weight: 1, fillColor: PRIMARY, fillOpacity: 0.08 }} />
              )}
              <CircleMarker center={[point.latitude, point.longitude]} radius={7} pathOptions={{ color: '#fff', weight: 2.5, fillColor: PRIMARY, fillOpacity: 1 }} />
            </MapContainer>
          </Box>

          <Box sx={{ flex: '999 1 300px', minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 1.5 }}>
            <Box sx={{ p: 1.75, px: 2, borderRadius: '12px', bgcolor: '#fff', border: `1px solid ${BORDER}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                <LocationOnOutlined sx={{ fontSize: 20, color: PRIMARY, flexShrink: 0 }} />
                <Typography variant="body2" sx={{ fontWeight: 600, color: point.address ? TEXT : '#94a3b8', lineHeight: 1.4, wordBreak: 'break-word' }}>
                  {point.address || 'Адрес не определен'}
                </Typography>
              </Box>
              {point.address && (
                <Tooltip title="Скопировать адрес" arrow>
                  <IconButton size="small" aria-label="Скопировать адрес" onClick={() => copy(point.address, 'Адрес скопирован')} sx={copyButtonSx}>
                    <ContentCopy sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            <Box sx={{ p: 1.25, px: 2, borderRadius: '12px', bgcolor: '#fff', border: `1px solid ${BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={monoSx}>{formatCoords(point)}</Typography>
                {point.accuracy != null && <Typography sx={{ color: MUTED, fontSize: '0.8rem' }}>±{Math.round(point.accuracy)} м</Typography>}
                <Tooltip title="Скопировать координаты" arrow>
                  <IconButton size="small" aria-label="Скопировать координаты" onClick={() => copy(formatCoords(point), 'Координаты скопированы')} sx={copyButtonSx}>
                    <ContentCopy sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  size="small"
                  component="a"
                  href={`https://2gis.kz/search/${point.latitude}%2C${point.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  endIcon={<OpenInNew sx={{ fontSize: 12 }} />}
                  sx={{ ...mapLinkSx, color: '#16a34a', bgcolor: '#f0fdf4', border: '1px solid #bbf7d0', '&:hover': { bgcolor: '#dcfce7' } }}
                >
                  2GIS
                </Button>
                <Button
                  size="small"
                  component="a"
                  href={`https://yandex.kz/maps/?pt=${point.longitude},${point.latitude}&z=17&l=map`}
                  target="_blank"
                  rel="noopener noreferrer"
                  endIcon={<OpenInNew sx={{ fontSize: 12 }} />}
                  sx={{ ...mapLinkSx, color: '#ea580c', bgcolor: '#fff7ed', border: '1px solid #fed7aa', '&:hover': { bgcolor: '#ffedd5' } }}
                >
                  Яндекс
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      ) : (
        <Box sx={{ p: 2, borderRadius: '12px', border: '1px dashed #cbd5e1', bgcolor: '#fff', display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <LocationOnOutlined sx={{ fontSize: 22, color: '#94a3b8', flexShrink: 0 }} />
          <Typography sx={{ color: MUTED, fontSize: '0.85rem', lineHeight: 1.5 }}>
            {unavailable
              ? 'Сервис геолокаций не отвечает, поэтому сохраненную точку сейчас не показать. Попробуйте позже кнопкой «Обновить данные» ниже.'
              : 'Точки на карте еще нет. Нажмите «Указать на карте»: мастер определит место и поможет поставить пин на вход в аптеку.'}
          </Typography>
        </Box>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth fullScreen={fullScreen} PaperProps={{ sx: { borderRadius: fullScreen ? 0 : '12px' } }}>
        <DialogTitle sx={{ px: { xs: 2, sm: 3 }, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, borderBottom: `1px solid ${BORDER}`, bgcolor: '#fff' }}>
          <Typography component="span" sx={{ fontWeight: 700, color: TEXT, fontSize: '1.05rem' }}>Местоположение аптеки</Typography>
          <IconButton aria-label="Закрыть" onClick={() => setOpen(false)} sx={{ color: '#94a3b8' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 2.5 }, '&&': { pt: { xs: 2, sm: 2.5 } } }}>
          <GeoWizard
            backend={profileGeoBackend}
            initial={point}
            onSaved={handleSaved}
            renderPhone={back => <PhoneHandoffPanel onSaved={handleSaved} onBack={back} />}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default GeolocationServiceCard;
